"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { CTAButton } from "@/shadcn/ui/button";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import BlackPill from "@/components/getting-started/BlackPill";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { PagePhemexOfferSchema } from "@/sanity/pages/PagePhemexOfferTemplate";
import { PageBitunixOfferSchema } from "@/sanity/pages/PageBitunixOfferTemplate";
import { PageTwitterSchema } from "@/sanity/pages/PageTwitterTemplate";

import logoFullImg from "@/public/lp2/global/logo-full.svg";
import gridGBimg from "@/public/lp2/green-banner/grid-bg.svg";
import blurBGimg from "@/public/lp2/green-banner/blur-bg.svg";
import cardBg from "@/public/lp2/green-banner/cardBg.png";
import cardBgPionex from "@/public/lp2/green-banner/card-bg-pionex.png";
import bitunixLogoImg from "@/public/lp2/global/logo-bitunix.svg";
import phemexLogoImg from "@/public/lp2/global/logo-phemex.svg";
import pionexLogoImg from "@/public/lp2/global/logo-phemex.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function LastSection({
  data,
  bitunix,
  phemex,
  pionex,
}: {
  data: PageHomeSchema | PagePhemexOfferSchema | PageBitunixOfferSchema | PageTwitterSchema;
  bitunix?: boolean;
  phemex?: boolean;
  pionex?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const contentRef = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const lastTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      lastTL
        .from(containerRef.current, {
          y: "2rem",
          delay: 0.5,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(contentRef.current.children, {
          y: "2rem",
          delay: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full">
      <div className="container-lp mx-auto lg:p-[20rem] pt-[12rem] pb-[8rem] !px-[2rem] lg:!px-[4rem]">
        <div className="green-card-section !h-full rounded-[3rem] flex flex-col items-center justify-center w-full py-[10rem] lg:py-[15rem] px-[2rem] lg:px-[13rem] relative">
          {/* Backgrounds */}
          {bitunix ? (
            <>
              <div className="green-card-section-blur-bitunix absolute z-[-2] mx-auto w-[80%] h-[65%] pointer-events-none rounded-[23.6875rem]"></div>
              <Image
                className="absolute !w-full !h-full object-top object-cover pointer-events-none rounded-[3rem]"
                alt="Card background"
                quality={100}
                src={cardBg}
              />
            </>
          ) : phemex ? (
            <>
              <div className="green-card-section-blur-phemex absolute z-[-2] mx-auto w-[80%] h-[65%] pointer-events-none rounded-[23.6875rem]"></div>
              <Image
                className="absolute !w-full !h-full object-top object-cover pointer-events-none rounded-[3rem]"
                alt="Card background"
                quality={100}
                src={cardBg}
              />
              <div className="absolute left-0 bottom-0 h-1/2 lg:h-1/3 w-full bg-gradient-to-t from-[#0B5BE0]/60 to-[#0B5BE0]/0 pointer-events-none blur-[2rem]"></div>
            </>
          ) : pionex ? (
            <>
              <div className="green-card-section-blur-pionex absolute z-[-2] mx-auto w-[80%] h-[65%] pointer-events-none rounded-[23.6875rem]"></div>
              <Image
                className="absolute !w-full !h-full object-top object-cover pointer-events-none rounded-[3rem]"
                alt="Card background"
                quality={100}
                src={cardBgPionex}
              />
            </>
          ) : (
            <>
              <Image
                className="absolute z-[-1] inset-0 w-full h-full pointer-events-none object-cover"
                alt="Grid background"
                src={gridGBimg}
              />
              <Image
                className="green-card-section-blur-img absolute z-[-2] inset-0 w-full h-full pointer-events-none"
                alt="Blur background"
                src={blurBGimg}
              />
              <div className="green-card-section-blur absolute z-[-2] mx-auto w-[80%] h-[65%] pointer-events-none rounded-[23.6875rem]"></div>
              <div className="absolute inset-0 z-[-2] pointer-events-none rounded-[4.4rem] overflow-clip">
                <HLSVideoPlayer
                  controls={false}
                  playsInline={true}
                  autoPlay={true}
                  muted={true}
                  loop={true}
                  className="!w-full !h-full object-cover pointer-events-none"
                  source={{
                    embed: false,
                    muxVideo: {
                      assetId: "",
                      filename: "video-bg",
                      status: "ready",
                      playbackId:
                        "T02VT02sMc9eTvJcCyP00FizzEcb2eSRE1kTJwO1r9y502g",
                    },
                    embedUrl: "",
                    thumbnail: {
                      src: "",
                      width: 0,
                      height: 0,
                      lqip: "",
                      name: "",
                    },
                  }}
                />
              </div>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 z-[2] w-[calc(100%-8rem)] h-[.2rem] bg-gradient-to-r from-white/0 via-[#00FF99]/50 to-white/0"></div>
            </>
          )}
          {/* End backgrounds */}

          <div
            ref={contentRef}
            className="w-full flex flex-col items-center justify-center relative"
          >
            {bitunix ? (
              <Image
                className="w-[36rem] lg:w-[54.625rem] mb-[5rem]"
                alt="Logo"
                src={bitunixLogoImg}
              />
            ) : phemex ? (
              <Image
                className="w-[36rem] lg:w-[54.625rem] mb-[5rem]"
                alt="Logo"
                src={phemexLogoImg}
              />
            ) : pionex ? (
              <Image
                className="w-[36rem] lg:w-[54.625rem] mb-[5rem]"
                alt="Logo"
                src={pionexLogoImg}
              />
            ) : (
              <Image
                className="h-[4.125rem] lg:h-[5.625rem] mb-[5rem]"
                alt="Logo"
                src={logoFullImg}
              />
            )}

            {data?.lastTexts && (
              <PortableText
                value={data?.lastTexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill
                        text={children as string}
                        className="text-[#ffffffd9] bg-[#34303066] border-[#ffffff38] uppercase backdrop-blur"
                      />
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-white text-[4rem] lg:text-[7rem] font-[600] tracking-[-0.16rem] lg:tracking-[-0.28rem] leading-[120%] text-center mt-[3rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-white text-center text-[2.25rem] font-[400] leading-[3.375rem] opacity-70 mb-[5rem] lg:w-[55%]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}

            <div
              className={
                bitunix
                  ? "[&_button]:[background:rgba(255,255,255,.84)!important] [&_.cta-thick-border_span]:[background:unset!important] mt-[3rem]"
                  : "mt-[3rem]"
              }
            >
              {data?.lastCTA && <CTAButton data={data?.lastCTA} />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
